// A stored Firestore document as the core's `DocField[]`, and back.
//
// #219, and #207's U2.
//
// ── Projection and carriage ────────────────────────────────────────────────
//
// There are two kinds of boundary into the core:
//
//   * `src/data/packageShape.js` turns a `packageEntries` document into
//     `core-ffi`'s `PackageEntry`: six **named, typed** fields.
//   * `src/domain/tombstone.js` turns any retracted document into the two
//     **scalars** `isActive(bool, Option<String>)` takes.
//
// They disagree about what a `Timestamp` becomes — an ISO string in one, the
// literal `'set'` in the other — because the core asks them different
// questions:
//
//   **If the core reads the value, the conversion is a projection and it is
//   per-type. If the core only carries the value, the conversion is carriage
//   and it can be shared.**
//
// `reconcileDocument` is carriage: `core/src/reconcile.rs::reconcile` matches
// on `field` and clones the winner; it never parses a value. So one conversion
// serves every collection, which is this file.
//
// ── Store to app is carriage too (#265) ────────────────────────────────────
//
// `toStoredDocument` / `fromStoredDocument` serve the boundary into and out of
// `src/data/appStore.js`, where the core is not involved. The store never
// reads a value either — every field it reads (`exists`, `order`, `complete`,
// `seenAt`) is about carriage — so it is this file's pair of functions.
//
// Here the encoding is not optional. The DocField crossing has a fast path —
// the overlay returns an unoverlaid document without calling either function.
// The store has none: both arms of `idbStore` copy a row through the
// structured clone algorithm (or a JSON copy where `structuredClone` is
// absent), which drops a custom prototype, so a `Timestamp` would come out as
// `{ seconds, nanoseconds }` with `toDate()` gone and no error. A reader
// guarding with `value?.toDate ? … : new Date(value)` would then produce an
// **Invalid Date**. Encoding on the way **in**, while the `Timestamp` still has
// its methods, leaves no class instance for a clone to strip.
//
// This is a recursive walk in JavaScript, not the wasm crossing #207 avoids per
// document: of the order of a microsecond or two per student document, against
// tens for the wasm boundary.
//
// ── Why this shape diagnoses better than the typed one ─────────────────────
//
// A wrong-typed field at a tsify record says `invalid type: string "10",
// expected i64` and **does not name the field** (#219). Every field of
// `DocField` is a `String`, so that failure cannot occur here. What can fail is
// a value that is not JSON, and this file catches that **before** the
// crossing, where it can name the document and the dotted sub-path.
// `test/core/docShape.wasm.test.js` asserts both halves against the real
// artifact.
//
// ── The keying matches `opCodec.encodeFields`, and it has to ───────────────
//
// `encodeFields` walks `Object.keys(data)` — **top level only** — and sets
// `value` to `JSON.stringify(value)`. `reconcile` pairs a remote field with a
// local one by exact name. So if this file flattened nested maps into dotted
// paths while the queue did not, `notes` on one side and `notes.student` on
// the other would never meet, and a per-field merge would silently keep the
// server's copy of every field a teacher had just edited. Top-level keys and
// whole-value JSON, the same as the queue.
//
// The dotted-key case that would break this pairing is unreachable: no code
// in `src/` calls `.update()` or `updateDoc` — every write is
// `batch.set(…, { merge: true })`, whose keys are top level. `opCodec.js`
// documents what happens to one anyway, and `test/ui/opCodec.test.jsx`'s "a
// dotted key stays dotted" pins it (#209, #254).
//
// ── What crosses, and what is refused ──────────────────────────────────────
//
// A closed whitelist, for the reason `opCodec.js` gives: a blacklist built on
// `JSON.stringify` turns a `Date` into a string, `NaN` into `null` and an
// `undefined` property into nothing, each a corrupted document with no error.
// The whitelists are *not* shared between the two files, and the difference is
// one type: `opCodec` refuses a `Timestamp` because a caller must not write
// one, and this file accepts one because Firestore hands one back.
//
// A `Timestamp` crosses **tagged** — `{"$timestamp":"…"}` — rather than as a
// bare ISO string. The tag is what makes the encoding injective, so that
// `fromDocFields` can hand the instant back as an instant rather than leaving a
// string where the provider had an object. Firestore is never imported here
// (the `ui` project cannot reach it, and the `core` project is bare Node), so
// the type is recognised by its `toDate()`, as `packageShape.js` recognises
// it, and reviving it is the **caller's** choice, passed in.
// `opCodec.decodeFields` takes its sentinel constructors the same way.
//
// ── An unresolved `serverTimestamp()` reads as null. It is not estimated. ──
//
// #207's Q4, held **by construction**: `queueFieldsToDocFields` maps the
// `serverTimestamp` transform to the JSON `null` and there is no branch that
// could produce a guess. A client clock would change the meaning of every
// reader built on "null means it has not landed yet" — `compareEntries`,
// `core/src/ledger.rs::is_active`, and #70's tombstones.
//
// What a null instant means is the reader's job: `HistoryTab.jsx` tests
// `updatedBy` first — a plain string that has a value the instant it is
// written — and keeps `updatedAt` in the condition for records without
// `updatedBy` (#200).
//
// ── The transforms that are not values ─────────────────────────────────────
//
// `deleteField`, `arrayUnion` and `arrayRemove` **cross, tagged** (#252,
// #288), and the meaning of the tag is the core's: `DocField` carries a fourth
// column and `core/src/reconcile.rs` resolves it — a `deleteField` by omitting
// the field, a union or removal by keeping the server's value and marking the
// row unresolved. That module's header has the argument.
//
// What *is* this file's: a `deleteField` row carries **no value at all**. An
// empty `value` and the JSON `null` are different rows, and `fromDocFields`
// will not turn either into the other: `null` is a value a teacher can store
// (`archivedAt` on every unarchived student), and "clear this" is not "store
// nothing here". Pinned in both jest projects.
//
// `arrayUnion` is written by `useEvents.jsx` (`exceptions`), `useTags.jsx`
// (`studentIds`, with `arrayRemove`) and `useMileage.jsx`. `deleteField` by
// `updateEntry` in src/data/useExpenses.jsx, which clears an expense's retired
// inline `attachments` / `attachment` in the same batch that converts them
// into `expenseAttachments` rows (#183). They are represented rather than
// avoided: a union is commutative where a read-modify-write of the whole array
// would lose a concurrent edit, and refusing a `deleteField` here would show
// the field still present while the write removes it.
//
// ── The crossing is linear, so overlay only ────────────────────────────────
//
// `reconcileDocument` costs tens of microseconds per document and grows
// linearly with its fields, with no ceiling. So the overlay set (a handful of
// documents) is reconciled, and never the whole store (tens of thousands of
// documents, about a second of blocked main thread).

/** The key that marks an instant inside an encoded value. */
export const TIMESTAMP_TAG = '$timestamp';

/** The code every refusal in this file carries, so a caller can tell one apart. */
export const UNCROSSABLE = 'coda/uncrossable-document';

/** The `transform` vocabulary, duplicated from `opCodec.js` — see below. */
const TRANSFORM_NONE = '';
const TRANSFORM_SERVER_TIMESTAMP = 'serverTimestamp';
const TRANSFORM_ENQUEUED_AT = 'enqueuedAt';
const TRANSFORM_DELETE_FIELD = 'deleteField';
const TRANSFORM_ARRAY_UNION = 'arrayUnion';
const TRANSFORM_ARRAY_REMOVE = 'arrayRemove';

/** Every transform this conversion can carry, for the test that pairs the lists. */
export const CROSSABLE_TRANSFORMS = [
  TRANSFORM_NONE,
  TRANSFORM_SERVER_TIMESTAMP,
  TRANSFORM_ENQUEUED_AT,
  TRANSFORM_DELETE_FIELD,
  TRANSFORM_ARRAY_UNION,
  TRANSFORM_ARRAY_REMOVE,
];

/**
 * The transforms that survive as far as a `DocField`, which is fewer.
 *
 * `serverTimestamp` and `enqueuedAt` are **resolved to values** on the way
 * across — `null` and the enqueue instant. #207's Q4 turns on the first
 * staying a value: a marker is something a later reader could decide to
 * estimate, and a `null` is not.
 */
export const DOC_FIELD_TRANSFORMS = [
  TRANSFORM_NONE,
  TRANSFORM_DELETE_FIELD,
  TRANSFORM_ARRAY_UNION,
  TRANSFORM_ARRAY_REMOVE,
];

function isPlainObject(value) {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * The instant this value denotes, or null if it does not denote one.
 *
 * A duck-type rather than an `instanceof Timestamp`, because this module must
 * not import `firebase/firestore`: the `ui` jest project has no way to reach
 * Firebase at all and that is the guarantee it exists to give. A `Date` is
 * accepted alongside a `Timestamp` — Firestore never stores one, but every test
 * double in this repo hands one over, and refusing it here would make the
 * module unexercisable without the SDK.
 */
function instantOf(value) {
  if (value instanceof Date) return value;
  if (value && typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date ? date : null;
  }
  return null;
}

/**
 * The instant this value denotes as an ISO string, or null if it denotes none.
 *
 * Exported because "what instant is this, without importing Firestore" is the
 * one decision the *typed* conversions share with this one;
 * `src/data/packageShape.js` calls it. `src/domain/tombstone.js` reduces an
 * instant to truthiness rather than to a time, so it does not.
 */
export function isoInstantOf(value) {
  const instant = instantOf(value);
  if (!instant || !Number.isFinite(instant.getTime())) return null;
  return instant.toISOString();
}

/** A refusal a person can act on, in `opCodec.js`'s shape and vocabulary. */
function refuse(message) {
  const error = new Error(`[docShape] ${message}`);
  error.code = UNCROSSABLE;
  return error;
}

/**
 * This value as JSON, or a throw naming where it stopped being JSON.
 *
 * `at` is the dotted sub-path used in the refusal, so a nested map —
 * `attendance.notes` is one this app really writes — names the leaf rather than
 * the document.
 */
function encodeValue(value, at, path) {
  if (value === null) return null;
  if (value === undefined) {
    throw refuse(
      `\`${at}\` in \`${path}\` is undefined. Firestore does not store undefined, so this is a `
      + 'document the caller assembled rather than one a snapshot delivered. Named here rather '
      + 'than dropped, which is what JSON.stringify would do.',
    );
  }
  const type = typeof value;
  if (type === 'string' || type === 'boolean') return value;
  if (type === 'number') {
    if (Number.isFinite(value)) return value;
    throw refuse(
      `\`${at}\` in \`${path}\` is ${String(value)}, which is not JSON. JSON.stringify would `
      + 'turn it into null, which is a different stored value and no error.',
    );
  }
  if (instantOf(value)) {
    const iso = isoInstantOf(value);
    if (!iso) throw refuse(`\`${at}\` in \`${path}\` is a timestamp with no valid instant in it.`);
    return { [TIMESTAMP_TAG]: iso };
  }
  if (Array.isArray(value)) return value.map((v, i) => encodeValue(v, `${at}[${i}]`, path));
  if (isPlainObject(value)) {
    // A stored map whose own key is the tag would decode as an instant. No
    // field in `src/` is written with a `$` key; it is refused anyway, because
    // the failure would be a silently retyped field.
    if (Object.prototype.hasOwnProperty.call(value, TIMESTAMP_TAG)) {
      throw refuse(
        `\`${at}\` in \`${path}\` has a \`${TIMESTAMP_TAG}\` key, which this encoding reserves `
        + 'for an instant. Rename the field.',
      );
    }
    const out = {};
    for (const key of Object.keys(value)) out[key] = encodeValue(value[key], `${at}.${key}`, path);
    return out;
  }
  throw refuse(
    `\`${at}\` in \`${path}\` is a ${value?.constructor?.name || type} and does not cross. A `
    + 'stored document is strings, finite numbers, booleans, null, timestamps, plain objects '
    + 'or arrays of those.',
  );
}

/** The inverse of `encodeValue`, with instants handed to `revive`. */
function decodeValue(value, revive) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => decodeValue(v, revive));
  if (Object.prototype.hasOwnProperty.call(value, TIMESTAMP_TAG)) {
    return revive(value[TIMESTAMP_TAG]);
  }
  const out = {};
  for (const key of Object.keys(value)) out[key] = decodeValue(value[key], revive);
  return out;
}

/**
 * One snapshot document as the plain JSON a local store can hold.
 *
 * The store-to-app boundary (#265) — see the header. The same carriage
 * `toDocFields` performs, minus the flattening into rows: the store holds
 * documents, not fields.
 *
 * After this runs, **nothing with a prototype is left in the document**. A
 * `Timestamp` has become `{"$timestamp":"…"}` while it still had its
 * `toDate()`, and anything else with a prototype — a `GeoPoint`, a
 * `DocumentReference`, a `Bytes` — is refused by name, so there is no class
 * instance left for a structured clone or a JSON copy to strip.
 *
 * `null` is preserved as `null` and `undefined` is refused; `JSON.stringify`
 * would drop the second silently.
 */
export function toStoredDocument(path, data) {
  if (!isPlainObject(data)) {
    throw refuse(
      `\`${path}\` was given ${data === null ? 'null' : typeof data} rather than a document`,
    );
  }
  // The top-level tag check `encodeValue` performs on every nested map. Without
  // it a field literally named `$timestamp` would make `fromStoredDocument`
  // revive the whole **document** into an instant. No field in `src/` is
  // written with a `$` key; refused anyway, as in `encodeValue`.
  if (Object.prototype.hasOwnProperty.call(data, TIMESTAMP_TAG)) {
    throw refuse(
      `\`${path}\` has a top-level \`${TIMESTAMP_TAG}\` field, which this encoding reserves for `
      + 'an instant. Rename the field.',
    );
  }
  const out = {};
  for (const key of Object.keys(data)) out[key] = encodeValue(data[key], key, path);
  return out;
}

/**
 * A stored document as the app should see it, with its instants revived.
 *
 * The inverse of `toStoredDocument` (#265).
 *
 * `reviveTimestamp` is the caller's, for the reason `fromDocFields` gives:
 * reconstructing a real Firestore `Timestamp` needs the SDK and this module may
 * not import it. The default hands the instant back as the ISO string it was
 * carried as — **a value whose wrongness is visible**: a reader that treats it
 * as a date gets a date; a reader that calls `.toDate()` on it throws rather
 * than quietly producing an Invalid Date.
 *
 * Never throws: a read on this path serves a whole collection, and everything
 * stored came through `toStoredDocument`.
 */
export function fromStoredDocument(data, { reviveTimestamp = (iso) => iso } = {}) {
  return decodeValue(data, reviveTimestamp);
}

/**
 * One stored document as the core's `DocField[]`.
 *
 * `path` is the full `users/{uid}/{collection}/{id}`, which is #207's store key
 * and also what `conflictClassFor` takes. `data` is exactly what `snap.data()`
 * delivered; nothing is normalised, because normalising is the provider's job
 * and this file does not know which provider it is serving.
 *
 * **Fields come out sorted by name.** `reconcile` preserves the remote list's
 * order and its header wants "two callers handing over the same document in a
 * different order" to agree — a property the fold cannot deliver on its own,
 * because it is a property of what it is handed (#255). Sorting here makes this
 * conversion a function of the document's contents rather than of whatever
 * order the SDK happened to enumerate its keys in. Nothing reads a `DocField`
 * by index, so the order costs nothing else.
 */
export function toDocFields(path, data) {
  if (!isPlainObject(data)) {
    throw refuse(
      `\`${path}\` was given ${data === null ? 'null' : typeof data} rather than a document`,
    );
  }
  return Object.keys(data)
    .sort()
    .map((field) => ({
      path,
      field,
      value: JSON.stringify(encodeValue(data[field], field, path)),
      // A stored document is values. Every row from a snapshot carries the
      // empty transform, and `reconcile` only ever reads the column on the
      // local side — see that module's header.
      transform: TRANSFORM_NONE,
    }));
}

/**
 * `DocField[]` back as a plain document.
 *
 * `reviveTimestamp` turns an encoded instant into whatever the caller wants one
 * to be, and defaults to the ISO string it was carried as. It is injected, as
 * `opCodec.decodeFields` takes its sentinel constructors: reconstructing a real
 * Firestore `Timestamp` needs the SDK, and this module may not import it. A
 * provider that hands its documents to code expecting a `Timestamp` should
 * pass `(iso) => Timestamp.fromDate(new Date(iso))`.
 *
 * No consumer in `src/` needs a `Timestamp` back: the calendar turns its
 * values with `asDate` in `src/components/calendar/weekGrid.js`, and every
 * value it sees is a `Date` the calendar fold *constructs* from a date-only
 * string and a wall-clock one (#269). `asDate` is the one function to change
 * if a document instant is routed into a calendar row.
 *
 * With the default, the round trip is **lossy for instants only**, and
 * visibly: an ISO string where an object was.
 *
 * **A `deleteField` row produces no key** (#252). `reconcile` already resolves
 * such a row by omitting it, so one reaching here is a local document never
 * folded — the overlay's own optimistic apply. Setting the key to `null`
 * instead would be indistinguishable from `archivedAt` on every unarchived
 * student.
 *
 * An `arrayUnion` row keeps its value, which after a fold is the **server's**
 * array — the union is still pending. A caller that wants to show the element
 * just added composes that itself from the queue rows it holds.
 */
export function fromDocFields(fields, { reviveTimestamp = (iso) => iso } = {}) {
  const out = {};
  for (const { path, field, value, transform } of fields || []) {
    if (transform === TRANSFORM_DELETE_FIELD) continue;
    if (value === '') {
      throw refuse(
        `\`${field}\` on \`${path}\` carries no value and no transform that supplies one. `
        + 'An empty value is not the JSON `null`, and this function will not decide that it is.',
      );
    }
    out[field] = decodeValue(JSON.parse(value), reviveTimestamp);
  }
  return out;
}

/**
 * The fields of one queued op as the core's `DocField[]` — the local side.
 *
 * Here rather than in the overlay (#207's U3) because this is where #207's Q4
 * is decided, by construction. The conversion **only**: choosing a conflict
 * class, dropping a row a queued delete tombstones, and marking `pendingWrite`
 * are the overlay's job.
 *
 * `value` passes through untouched for an ordinary field, because
 * `opCodec.encodeFields` already wrote it with `JSON.stringify`; re-encoding
 * it would let the two vocabularies drift.
 *
 * The transform names are duplicated from `opCodec.js` rather than imported,
 * for the reason that file gives about `ENQUEUED_AT_FIELD`: importing would
 * pull `firebase/firestore`'s `FieldValue` in with them, and this module is
 * usable in the `core` and `ui` projects because it imports nothing.
 * `test/core/docShape.wasm.test.js` asserts the two vocabularies agree.
 */
export function queueFieldsToDocFields(path, fields) {
  return (fields || []).map(({ field, value, transform }) => {
    if (transform === TRANSFORM_NONE) {
      return { path, field, value, transform: TRANSFORM_NONE };
    }
    if (transform === TRANSFORM_SERVER_TIMESTAMP) {
      // #207 Q4. Not an estimate. It resolves to a *value* rather than to a
      // tag: a tag is something a later reader could decide to fill in, and
      // `null` is not.
      return { path, field, value: 'null', transform: TRANSFORM_NONE };
    }
    if (transform === TRANSFORM_ENQUEUED_AT) {
      // Already resolved at enqueue (#232), and a real instant, so it crosses
      // as one — the encoding the stored field will have once it lands.
      return {
        path,
        field,
        value: JSON.stringify({ [TIMESTAMP_TAG]: new Date(JSON.parse(value)).toISOString() }),
        transform: TRANSFORM_NONE,
      };
    }
    if (transform === TRANSFORM_DELETE_FIELD) {
      // #252. The empty value is not a placeholder for `null`; it is the
      // absence of a value, and the transform carries the meaning.
      // `opCodec.encodeFields` writes the row this way too — the sentinel has
      // no payload.
      return { path, field, value: '', transform: TRANSFORM_DELETE_FIELD };
    }
    if (transform === TRANSFORM_ARRAY_UNION || transform === TRANSFORM_ARRAY_REMOVE) {
      // #252, #288. The elements pass through untouched, as the queue stored
      // them. `reconcile` does **not** propagate them — it keeps
      // the server's array and tags the row — so this value is the local side's
      // alone, and it is here for a caller that wants to show what is pending
      // rather than for the fold.
      //
      // For `arrayRemove` that distinction is the difference between a stale
      // screen and a false one: the value is the members being **taken out**,
      // never a membership, so anything that rendered it as the field's value
      // would show a tag's last removed student as its only member.
      return { path, field, value, transform };
    }
    throw refuse(
      `\`${field}\` on \`${path}\` carries the \`${transform}\` transform, which a DocField `
      + 'cannot express. The four that cross are an ordinary value, a field removal, a union '
      + 'and a member removal; `increment` is refused at enqueue by `core/src/queue.rs` and '
      + 'has no call site, and a transform newer than this file is refused rather than '
      + 'quietly carried.',
    );
  });
}
